import { useEffect } from "react";

import { Decimal, LiquityStoreState, LQTYStake } from "@liquity/lib-base";
import { LiquityStoreUpdate, useLiquityReducer } from "@liquity/lib-react";

import { useMyTransactionState } from "../../Transaction";

import { StakingViewAction, StakingViewContext2 } from "./StakingViewContext";

type StakingViewProviderAction =
  | LiquityStoreUpdate
  | StakingViewAction
  | { type: "startChange" | "abortChange" };

type StakingViewProviderState = {
  lqtyStake: LQTYStake;
  changePending: boolean;
  adjusting: boolean;
  redeem: boolean;
  sPrintBalance: Decimal;
};

const init = ({ lqtyStake, sPrintBalance }: LiquityStoreState): StakingViewProviderState => ({
  lqtyStake,
  sPrintBalance,
  changePending: false,
  adjusting: false,
  redeem: false,
});

const reduce = (
  state: StakingViewProviderState,
  action: StakingViewProviderAction
): StakingViewProviderState => {
  // console.log(state);
  // console.log(action);

  switch (action.type) {
    case "startAdjusting":
      return { ...state, adjusting: true };

    case "startRedeem":
      return { ...state, redeem: true };

    case "cancelAdjusting":
      return { ...state, adjusting: false, redeem: false };

    case "startChange":
      return { ...state, changePending: true };

    case "abortChange":
      return { ...state, changePending: false, redeem: false, adjusting: false};

    case "updateStore": {
      const {
        oldState: { lqtyStake: oldStake },
        stateChange: { lqtyStake: updatedStake }
      } = action;

      if (updatedStake) {
        const changeCommitted =
          !updatedStake.stakedLQTY.eq(oldStake.stakedLQTY) ||
          updatedStake.collateralGain.lt(oldStake.collateralGain) ||
          updatedStake.lusdGain.lt(oldStake.lusdGain);

        return {
          ...state,
          sPrintBalance: state.sPrintBalance, // Update balance if available
          lqtyStake: updatedStake,
          adjusting: false,
          redeem: false,
          changePending: changeCommitted ? false : state.changePending
        };
      }
    }
  }

  return state;
};

export const StakingViewProvider2: React.FC = ({ children }) => {
  const stakingTransactionState = useMyTransactionState("sprint");
  const [{ adjusting, changePending, sPrintBalance, redeem }, dispatch] = useLiquityReducer(reduce, init);

  useEffect(() => {
    if (
      stakingTransactionState.type === "waitingForApproval" ||
      stakingTransactionState.type === "waitingForConfirmation"
    ) {
      dispatch({ type: "startChange" });
    } else if (
      stakingTransactionState.type === "failed" ||
      stakingTransactionState.type === "cancelled" ||
      stakingTransactionState.type === "confirmedOneShot"  // 👈 ADD THIS
    ) {
      dispatch({ type: "abortChange" });
    }
  }, [stakingTransactionState.type, dispatch]);

  return (
    <StakingViewContext2.Provider
      value={{
        view:  adjusting ? "ADJUSTING" : redeem ? "REDEEM" : sPrintBalance.isZero ? "NONE" : "ACTIVE",
        changePending,
        dispatch
      }}
    >
      {children}
    </StakingViewContext2.Provider>
  );
};
